import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError
} from 'axios';
import {
  API_BASE_URL,
  API_TIMEOUT,
  DEFAULT_HEADERS,
  AUTH_TOKEN_KEY,
  HTTP_STATUS
} from '@/shared/config/api';

class HttpClient {
  private instance: AxiosInstance;
  private onTokenExpired?: () => void;
  private isLoggingOut = false;

  constructor() {
    this.instance = axios.create({
      baseURL: API_BASE_URL,
      timeout: API_TIMEOUT,
      headers: DEFAULT_HEADERS
    });

    this.setupInterceptors();
  }

  public setTokenExpirationHandler(handler: () => void): void {
    this.onTokenExpired = handler;
  }

  /**
   * Настройка перехватчиков запросов и ответов
   */
  private setupInterceptors(): void {
    // Перехватчик запросов - добавляем токен авторизации
    this.instance.interceptors.request.use(
      (config) => {
        const token = this.getAuthToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    this.instance.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response?.status === HTTP_STATUS.UNAUTHORIZED && !this.isLoggingOut) {
          if (this.onTokenExpired) {
            this.onTokenExpired();
          } else {
            this.clearAuthData();
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * Получить токен авторизации из localStorage
   */
  private getAuthToken(): string | null {
    return localStorage.getItem(AUTH_TOKEN_KEY);
  }

  private clearAuthData(): void {
    localStorage.removeItem(AUTH_TOKEN_KEY);
  }

  public setAuthToken(token: string): void {
    if (token) {
      localStorage.setItem(AUTH_TOKEN_KEY, token);
      this.isLoggingOut = false;
    } else {
      localStorage.removeItem(AUTH_TOKEN_KEY);
      this.isLoggingOut = true;
    }
  }

  /**
   * GET запрос
   */
  public async get<T = unknown>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.instance.get<T>(url, config);
  }

  /**
   * POST запрос
   */
  public async post<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.instance.post<T>(url, data, config);
  }

  /**
   * PUT запрос
   */
  public async put<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.instance.put<T>(url, data, config);
  }

  /**
   * PATCH запрос
   */
  public async patch<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.instance.patch<T>(url, data, config);
  }

  /**
   * DELETE запрос
   */
  public async delete<T = unknown>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.instance.delete<T>(url, config);
  }

  /**
   * Загрузка файла. timeout — для больших файлов: общий таймаут клиента
   * (API_TIMEOUT, 10 с) на медленной связи не успевает отправить фото.
   */
  public async uploadFile<T = unknown>(
    url: string,
    file: File,
    fieldName = 'file',
    onUploadProgress?: (progressEvent: {
      loaded: number;
      total?: number;
    }) => void,
    timeout?: number
  ): Promise<AxiosResponse<T>> {
    const formData = new FormData();
    formData.append(fieldName, file);

    return this.instance.post<T>(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      ...(onUploadProgress && { onUploadProgress }),
      ...(timeout !== undefined && { timeout })
    });
  }

  /**
   * Получить прямой доступ к экземпляру axios (для особых случаев)
   */
  public getInstance(): AxiosInstance {
    return this.instance;
  }
}

/**
 * Экспортируем единственный экземпляр HTTP клиента
 */
export const httpClient = new HttpClient();
